import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { TOKEN_KEY, ACT_AS_KEY } from '@/core/storage'
import { MOCK_USER } from '@/core/seed'
import { USE_API } from '@/core/config'
import { loginApi, fetchMeApi } from '@/core/api/auth'
import { registerUserDisplayName } from '@/modules/content/constants'
import { resolveUserRoleBadge } from '@/modules/auth/roleBadge'

function loadActAs() {
  try {
    const raw = localStorage.getItem(ACT_AS_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem(TOKEN_KEY) || '')
  const user = ref(null)
  const actAs = ref(loadActAs())
  const landing = ref(null)

  const isLoggedIn = computed(() => Boolean(token.value))
  const isPlatformSuper = computed(() => Boolean(user.value?.isSuper))
  const isSuper = computed(
    () => isPlatformSuper.value && !actAs.value?.systemId
  )
  const currentSystemId = computed(
    () => actAs.value?.systemId ?? user.value?.systemId ?? null
  )
  const actAsSystemId = computed(() => actAs.value?.systemId ?? null)
  const actAsSystemName = computed(() => actAs.value?.systemName ?? null)
  const canManageAccounts = computed(
    () =>
      Boolean(user.value?.canManageAccounts) || Boolean(user.value?.isSuper)
  )
  const userRoleBadge = computed(() => resolveUserRoleBadge(user.value))

  function mapUser(me) {
    const isSuper = Boolean(me.isSuper) || me.role === 'SUPER'
    const canManageAccounts =
      Boolean(me.canManageAccounts) || isSuper
    const mapped = {
      id: me.id,
      username: me.username,
      displayName: me.displayName,
      avatarUrl: me.avatarUrl,
      role: me.role || (isSuper ? 'SUPER' : 'USER'),
      canManageAccounts,
      isSuper,
      systemId: me.systemId ?? null,
      systemCode: me.systemCode ?? null,
      systemName: me.systemName ?? null
    }
    registerUserDisplayName(mapped.id, mapped.displayName)
    return mapped
  }

  async function login(username, password, systemId, systemName) {
    if (USE_API) {
      try {
        const res = await loginApi(username, password, systemId)
        token.value = res.token
        user.value = mapUser(res.user)
        landing.value = res.landing || '/home'
        localStorage.setItem(TOKEN_KEY, token.value)
        if (user.value.isSuper) {
          if (systemId) {
            enterSystem(systemId, systemName || '')
          } else {
            exitActAs()
          }
        }
        return { code: 0, message: 'ok', landing: landing.value }
      } catch (e) {
        return { code: 1, message: e.message || '登录失败' }
      }
    }
    if (username === MOCK_USER.username && password === MOCK_USER.password) {
      token.value = 'mock-jwt-token'
      localStorage.setItem(TOKEN_KEY, token.value)
      user.value = mapUser({
        ...MOCK_USER,
        role: 'SUPER',
        canManageAccounts: true,
        isSuper: true,
        status: 'ACTIVE',
        systemId: systemId ?? null
      })
      landing.value =
        user.value.isSuper && !systemId ? '/admin/systems' : '/home'
      if (user.value.isSuper) {
        if (systemId) {
          enterSystem(systemId, systemName || '演示系统')
        } else {
          exitActAs()
        }
      }
      return { code: 0, message: 'ok', landing: landing.value }
    }
    return { code: 1, message: '账号或密码错误' }
  }

  function enterSystem(systemId, systemName) {
    actAs.value = { systemId, systemName, enteredAt: Date.now() }
    localStorage.setItem(ACT_AS_KEY, JSON.stringify(actAs.value))
  }

  function exitActAs() {
    actAs.value = null
    localStorage.removeItem(ACT_AS_KEY)
  }

  function logout() {
    token.value = ''
    user.value = null
    actAs.value = null
    landing.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(ACT_AS_KEY)
  }

  async function fetchMe() {
    if (!token.value) return
    if (USE_API) {
      try {
        const me = await fetchMeApi()
        user.value = mapUser(me)
        actAs.value = loadActAs()
      } catch {
        logout()
      }
      return
    }
    user.value = mapUser({
      ...MOCK_USER,
      role: 'SUPER',
      canManageAccounts: true,
      isSuper: true,
      status: 'ACTIVE'
    })
    actAs.value = loadActAs()
  }

  if (token.value) {
    fetchMe()
  }

  return {
    token,
    user,
    actAs,
    landing,
    isLoggedIn,
    isPlatformSuper,
    isSuper,
    currentSystemId,
    actAsSystemId,
    actAsSystemName,
    canManageAccounts,
    userRoleBadge,
    login,
    logout,
    fetchMe,
    enterSystem,
    exitActAs
  }
})
